import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Checkbox, MenuItem, Stack, useTheme, Tooltip, Box } from '@mui/material';
import { memo } from 'react';
import { getCheckboxSx, InputChoice } from '../dropdownConsts';
import { SelectDropdownInputProps, PossibleEvent } from './SelectDropdownInput';
import { LabeledItem } from 'components/FormControls/ListSearchSelect/LabeledItem';

export interface SelectMenuItemProps {
  inputLabel: SelectDropdownInputProps['inputLabel'];
  choice: InputChoice;
  value: string | string[];
  handleSelectionChange: (event: PossibleEvent, selectedValue: string) => void;
  intersectionRef?: any; // mantine useIntersection hook ref - load more observer on last item
  isDataPrivate?: boolean;
  group?: SelectDropdownInputProps['group'];
  multiple?: boolean;
  disabled?: boolean;
  cycleSelectionState?: Record<string, 'add' | 'remove'>;
}

export const SelectMenuItem = memo(
  ({
    inputLabel,
    choice,
    value,
    handleSelectionChange,
    intersectionRef,
    isDataPrivate,
    multiple,
    disabled,
    group,
  }: SelectMenuItemProps) => {
    // Even though this is single-select, the checkbox had better keyboard nav vs the radio.
    // So we'll use the checkbox for the single select and style it like a radio.
    const theme = useTheme();
    const checked = Array.isArray(value)
      ? value.map(String).includes(String(choice?.value))
      : String(value) === String(choice?.value);

    // Check if this specific choice is disabled OR if the entire dropdown is disabled
    const isDisabled = disabled || choice?.disabled === true;

    return (
      <MenuItem
        data-testid={`select-dropdown-input-menu-item--${choice?.value}`}
        ref={intersectionRef}
        value={choice?.value}
        onClick={(event) => handleSelectionChange(event, choice.value)}
        disableGutters={true}
        disableRipple={true}
        disabled={isDisabled}
        sx={{
          '& .Mui-focusVisible': {
            backgroundColor: theme.palette.action.focus,
          },
          minHeight: 'auto',
          alignItems: 'center',
          [theme.breakpoints.down('sm')]: {
            minHeight: 'auto', // Override MUI Default
          },
        }}
      >
        <Checkbox
          size="small"
          disabled={isDisabled}
          data-private={isDataPrivate ? true : undefined}
          icon={
            multiple ? (
              <CheckBoxOutlineBlankIcon color="info" />
            ) : (
              <RadioButtonUncheckedIcon color="info" />
            )
          }
          checkedIcon={
            multiple ? (
              <CheckBoxIcon color="primary" />
            ) : (
              <RadioButtonCheckedIcon color="primary" />
            )
          }
          disableRipple={true}
          aria-multiselectable={true}
          aria-checked={checked}
          checked={checked}
          value={choice?.value}
          name={`${inputLabel}-${multiple ? 'checkbox' : 'radio'}`}
          sx={getCheckboxSx}
          slotProps={{
            input: {
              'aria-label': `${choice?.label}-${multiple ? 'checkbox' : 'radio'}`,
            },
          }}
        />
        <Stack
          direction="column"
          sx={{
            width: '100%',
          }}
        >
          <Tooltip
            disableInteractive={true}
            arrow={true}
            enterDelay={500}
            leaveDelay={0}
            placement="bottom"
            title={choice?.description || undefined}
            slotProps={{
              popper: {
                'data-private': isDataPrivate ? true : undefined,
              } as any,
            }}
          >
            <Box>
              <LabeledItem
                primaryText={choice.label}
                secondaryText={choice.sublabel}
                chip={choice.chip}
                isDataPrivate={isDataPrivate}
                size="regular"
              />
            </Box>
          </Tooltip>
        </Stack>
      </MenuItem>
    );
  },
);
SelectMenuItem.displayName = 'SelectMenuItem';
